import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MdClose, MdCloudUpload, MdCheckCircle } from "react-icons/md";
import Swal from "sweetalert2";

const RubriqueModal = ({
                         isOpen,
                         onClose,
                         onSubmit,
                         rubrique,
                         isLoading,
                         error,
                       }) => {
  const [formData, setFormData] = useState({
    name_rub: "",
    description_rub: "",
    photo_rub: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (rubrique) {
      setFormData({
        name_rub: rubrique.name_rub || "",
        description_rub: rubrique.description_rub || "",
        photo_rub: null, // Ne pas remplir automatiquement le champ de fichier
      });
      setImagePreview(rubrique.photo_rub || null);
    } else {
      setFormData({
        name_rub: "",
        description_rub: "",
        photo_rub: null,
      });
      setImagePreview(null);
    }
    setValidationErrors({});
  }, [rubrique, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Effacer l'erreur lorsque l'utilisateur corrige le champ
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: undefined });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo_rub: file });

      // Générer un aperçu de l'image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Effacer l'erreur si elle existe
      if (validationErrors.photo_rub) {
        setValidationErrors({ ...validationErrors, photo_rub: undefined });
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name_rub.trim()) {
      errors.name_rub = "Le nom de la rubrique est requis";
    }
    if (!formData.description_rub.trim()) {
      errors.description_rub = "La description est requise";
    }
    if (!rubrique && !formData.photo_rub && !imagePreview) {
      errors.photo_rub = "Une image est requise";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Afficher SweetAlert pour le chargement
      Swal.fire({
        title: rubrique ? "Mise à jour en cours..." : "Création en cours...",
        html: `
          <div class="flex flex-col items-center">
            <div class="relative w-20 h-20">
              <div class="absolute top-0 left-0 w-20 h-20 border-4 border-[#57A863] border-opacity-20 rounded-full"></div>
              <div class="absolute top-0 left-0 w-20 h-20 border-4 border-transparent border-t-[#57A863] rounded-full animate-spin"></div>
            </div>
            <p class="mt-4 text-gray-700">Veuillez patienter pendant le traitement...</p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Créer un FormData correct
      const dataToSubmit = new FormData();

      // S'assurer que les valeurs sont explicitement converties en string
      dataToSubmit.append("name_rub", formData.name_rub.toString());
      dataToSubmit.append(
          "description_rub",
          formData.description_rub.toString()
      );

      // Ajouter l'image seulement si elle existe
      if (formData.photo_rub instanceof File) {
        // Vérifier le type MIME du fichier
        const fileType = formData.photo_rub.type;
        const validTypes = [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/gif",
          "image/svg+xml",
        ];

        if (!validTypes.includes(fileType)) {
          Swal.close();
          setValidationErrors({
            ...validationErrors,
            photo_rub:
                "Le fichier doit être une image (jpeg, png, jpg, gif, svg).",
          });
          return;
        }

        // Vérifier la taille
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (formData.photo_rub.size > maxSize) {
          Swal.close();
          setValidationErrors({
            ...validationErrors,
            photo_rub: "L'image ne doit pas dépasser 10MB.",
          });
          return;
        }

        // Ajouter au formData si valide
        dataToSubmit.append("photo_rub", formData.photo_rub);
      } else if (!rubrique && !imagePreview) {
        // Si c'est une création et qu'il n'y a pas d'image
        Swal.close();
        setValidationErrors({
          ...validationErrors,
          photo_rub: "Une image est requise pour créer une rubrique.",
        });
        return;
      }


      onSubmit(dataToSubmit)
          .then(() => {
            // Fermer SweetAlert de chargement
            Swal.close();

            // Afficher message de succès
            Swal.fire({
              icon: "success",
              title: rubrique ? "Rubrique mise à jour!" : "Rubrique ajoutée!",
              text: rubrique
                  ? "La rubrique a été modifiée avec succès."
                  : "La nouvelle rubrique a été ajoutée avec succès.",
              confirmButtonColor: "#57A863",
              showClass: {
                popup: "animate__animated animate__fadeInUp faster",
              },
              hideClass: {
                popup: "animate__animated animate__fadeOutDown faster",
              },
            });
          })
          .catch((err) => {
            // Fermer SweetAlert de chargement
            Swal.close();

            // Créer un message d'erreur plus instructif
            let errorMessage = "Une erreur est survenue lors de l'opération.";

            if (err.message) {
              if (typeof err.message === "object") {
                // Pour les erreurs de validation Laravel
                const messages = [];
                Object.entries(err.message).forEach(([field, fieldErrors]) => {
                  if (Array.isArray(fieldErrors)) {
                    messages.push(...fieldErrors);
                  } else {
                    messages.push(fieldErrors);
                  }
                });
                errorMessage = messages.join("\n");
              } else {
                errorMessage = err.message;
              }
            }

            // Afficher l'erreur
            Swal.fire({
              icon: "error",
              title: "Erreur!",
              text: errorMessage,
              confirmButtonColor: "#d33",
            });
          });
    }
  };

  // Gestion du drag & drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setFormData({ ...formData, photo_rub: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      if (validationErrors.photo_rub) {
        setValidationErrors({ ...validationErrors, photo_rub: undefined });
      }
    }
  };

  return (
      <AnimatePresence>
        {isOpen && (
            <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
              <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                  className="relative w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
              >
                {/* Header - Amélioré avec gradient */}
                <div className="relative bg-gradient-to-r from-[#57A863] to-[#3d9448] px-6 py-5 text-white">
                  <h3 className="text-xl font-bold">
                    {rubrique ? "Modifier la rubrique" : "Ajouter une rubrique"}
                  </h3>
                  <button
                      onClick={onClose}
                      className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-1.5 text-white transition-all hover:rotate-90 hover:bg-white/30"
                      aria-label="Fermer"
                  >
                    <MdClose className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Grille à deux colonnes pour écrans plus larges */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {/* Colonne de gauche - Champs textuels */}
                      <div className="space-y-6">
                        <div>
                          <label
                              htmlFor="name_rub"
                              className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            Nom de la rubrique
                            <span className="ml-1 text-red-500">*</span>
                          </label>
                          <input
                              type="text"
                              id="name_rub"
                              name="name_rub"
                              value={formData.name_rub}
                              onChange={handleInputChange}
                              className={`w-full rounded-lg border ${
                                  validationErrors.name_rub
                                      ? "border-red-500 ring-1 ring-red-500"
                                      : "border-gray-300"
                              } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                              placeholder="Ex: Habiter"
                          />
                          {validationErrors.name_rub && (
                              <p className="mt-1 flex items-center text-sm text-red-600">
                                <svg
                                    className="mr-1 h-4 w-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                  <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                      clipRule="evenodd"
                                  />
                                </svg>
                                {validationErrors.name_rub}
                              </p>
                          )}
                        </div>

                        <div>
                          <label
                              htmlFor="description_rub"
                              className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            Description<span className="ml-1 text-red-500">*</span>
                          </label>
                          <textarea
                              id="description_rub"
                              name="description_rub"
                              value={formData.description_rub}
                              onChange={handleInputChange}
                              rows={4}
                              className={`w-full rounded-lg border ${
                                  validationErrors.description_rub
                                      ? "border-red-500 ring-1 ring-red-500"
                                      : "border-gray-300"
                              } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                              placeholder="Décrivez la rubrique ici..."
                          ></textarea>
                          {validationErrors.description_rub && (
                              <p className="mt-1 flex items-center text-sm text-red-600">
                                <svg
                                    className="mr-1 h-4 w-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                  <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                      clipRule="evenodd"
                                  />
                                </svg>
                                {validationErrors.description_rub}
                              </p>
                          )}
                        </div>
                      </div>

                      {/* Colonne de droite - Upload d'image amélioré */}
                      <div className="flex flex-col items-center space-y-4">
                        {/* Aperçu de l'image */}
                        {imagePreview ? (
                            <div className="group relative h-40 w-40 overflow-hidden rounded-full border-4 border-gray-200 shadow-md transition-all duration-300 hover:border-[#57A863]">
                              <img
                                  src={imagePreview}
                                  alt="Aperçu"
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                              />
                              <div className="bg-black/50 absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                      setImagePreview(null);
                                      setFormData((prev) => ({
                                        ...prev,
                                        photo_rub: null,
                                      }));
                                    }}
                                    className="rounded-full bg-red-500 p-2 text-white hover:bg-red-600"
                                >
                                  <MdClose className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                        ) : (
                            <div className="flex h-40 w-40 items-center justify-center rounded-full border-4 border-gray-200 bg-gray-50">
                        <span className="text-sm text-gray-500">
                          Aucune image
                        </span>
                            </div>
                        )}

                        {/* Zone de drag & drop améliorée */}
                        <div
                            className={`relative flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed 
                        ${
                                isDragging
                                    ? "border-[#57A863] bg-green-50"
                                    : validationErrors.photo_rub
                                        ? "border-red-300 bg-red-50"
                                        : "border-gray-300 bg-gray-50"
                            }
                        p-6 transition-all duration-300 hover:border-[#57A863] hover:bg-green-50`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                          <input
                              type="file"
                              id="photo_rub"
                              name="photo_rub"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                          />
                          <MdCloudUpload
                              className={`mb-2 h-10 w-10 ${
                                  isDragging
                                      ? "text-[#57A863]"
                                      : validationErrors.photo_rub
                                          ? "text-red-400"
                                          : "text-gray-400"
                              }`}
                          />
                          <p
                              className={`text-center text-sm ${
                                  isDragging
                                      ? "text-[#57A863]"
                                      : validationErrors.photo_rub
                                          ? "text-red-500"
                                          : "text-gray-500"
                              }`}
                          >
                            {formData.photo_rub
                                ? formData.photo_rub.name
                                : isDragging
                                    ? "Déposez l'image ici"
                                    : "Cliquez ou glissez une image ici"}
                          </p>
                          <p className="mt-1 text-center text-xs text-gray-400">
                            PNG, JPG, GIF jusqu'à 10MB
                          </p>

                          {validationErrors.photo_rub && (
                              <p className="absolute -bottom-6 mt-1 flex items-center text-sm text-red-500">
                                <svg
                                    className="mr-1 h-4 w-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                  <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                      clipRule="evenodd"
                                  />
                                </svg>
                                {validationErrors.photo_rub}
                              </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Message d'erreur du serveur */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="rounded-lg bg-red-50 p-4 text-sm text-red-700"
                        >
                          <div className="flex">
                            <svg
                                className="mr-2 h-5 w-5 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                              <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                              />
                            </svg>
                            <div>
                              <h3 className="font-medium">Une erreur est survenue</h3>
                              <div className="mt-1">{error}</div>
                            </div>
                          </div>
                        </motion.div>
                    )}
                  </form>
                </div>

                {/* Footer - Amélioré avec ombres et transitions */}
                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2"
                        disabled={isLoading}
                    >
                      Annuler
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-5 py-2.5 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 disabled:opacity-70"
                        disabled={isLoading}
                    >
                      {isLoading ? (
                          <span className="flex items-center justify-center">
                      <svg
                          className="mr-2 h-4 w-4 animate-spin"
                          viewBox="0 0 24 24"
                      >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                        ></circle>
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Traitement...
                    </span>
                      ) : (
                          <span className="flex items-center">
                      <MdCheckCircle className="mr-1.5 h-4 w-4" />
                            {rubrique ? "Mettre à jour" : "Ajouter"}
                    </span>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
        )}
      </AnimatePresence>
  );
};

export default RubriqueModal;